import { useEffect, useState, useCallback, useRef } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { DictationMic } from "@/components/DictationMic";
import { RephraseStyleMenu } from "@/components/TextAssist";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Megaphone, Send, AlertTriangle, Mail, MessageSquareText, Users, ShieldAlert, CalendarClock, RotateCcw, Pencil, X, MoonStar } from "lucide-react";
import { AudiencePicker } from "./messages/AudiencePicker";
import { GroupsDialog } from "./messages/GroupsDialog";
import { CopyEditor } from "./messages/CopyEditor";
import { MessageHistory } from "./messages/MessageHistory";
import { DeliveryModePicker, modeLabel } from "./messages/DeliveryMode";
import { StepHeader, StepBody, WizardFooter, MessagesIntro } from "./messages/Wizard";
import { fmtDate, tzLabel, toLocalInput, minLocalInput } from "./messages/time";
import { V } from "@/lib/vocab";

const EMPTY_AUD = { type: "all", keys: [], group_id: "", people: [] };
const HIST_PAGE = 25;

const Pill = ({ on, onClick, icon: Icon, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors ${on ? "border-ink bg-ink text-white" : "border-sand-200 bg-white text-ink/60 hover:border-ink"}`}>
    <Icon className="h-3.5 w-3.5" /> {children}
  </button>
);

const skipSummary = (sk = {}) => [
  sk.sms_not_opted_in && `${sk.sms_not_opted_in} not opted in to texts`, sk.sms_stopped && `${sk.sms_stopped} texted STOP`,
  sk.sms_no_phone && `${sk.sms_no_phone} no mobile`, sk.sms_pref_off && `${sk.sms_pref_off} texts off in their settings`,
  sk.email_none && `${sk.email_none} no email`, sk.email_pref_off && `${sk.email_pref_off} email off in their settings`,
].filter(Boolean).join(" · ");

export default function Broadcasts() {
  const { tenantId, tenant } = useDash();
  const tz = tenant?.timezone || "";
  const [kind, setKind] = useState("announcement");
  const [audience, setAudience] = useState(EMPTY_AUD);
  const [mode, setMode] = useState("email_first");
  const [sameCopy, setSameCopy] = useState(true);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [smsBody, setSmsBody] = useState("");
  const [schedule, setSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState({});
  const [groups, setGroups] = useState([]);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [history, setHistory] = useCachedState(`bcast-hist:${tenantId}`, []);
  const [histMore, setHistMore] = useState(false);
  const [histLoadingMore, setHistLoadingMore] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [rephrasing, setRephrasing] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [preRephrase, setPreRephrase] = useState(null);
  const [pendingRe, setPendingRe] = useState(null);
  const topRef = useRef(null);

  const personal = audience.type === "people";
  const effKind = audience.type === "all" ? kind : "announcement";
  const urgent = effKind === "safety";
  const effMode = urgent ? "both" : mode;
  const channels = ["sms", "email"];
  const split = !sameCopy;
  const smsText = split ? smsBody : message;
  const peopleIds = (audience.people || []).map((p) => p.id);
  const audienceReady = audience.type === "all" || (audience.type === "group" && !!audience.group_id) || (audience.type === "people" && peopleIds.length > 0)
    || (audience.type === "audiences" && (audience.keys || []).length > 0);

  const payload = useCallback(() => ({
    kind: effKind, channels, delivery_mode: effMode, same_copy: !split, title, body: message, sms_body: smsText,
    audience: { type: audience.type, group_id: audience.group_id || undefined, person_ids: peopleIds, keys: audience.keys || [] },
    ...(schedule && scheduleAt ? { scheduled_at: scheduleAt } : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [effKind, effMode, split, title, message, smsText, audience.type, audience.group_id, peopleIds.join(","), (audience.keys || []).join(","), schedule, scheduleAt]);

  const loadGroups = useCallback(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/groups`).then((r) => setGroups(r.data || [])).catch(() => setGroups([]));
  }, [tenantId]);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  const loadHistory = useCallback(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/broadcasts?limit=${HIST_PAGE}&offset=0`)
      .then((r) => { const rows = r.data || []; setHistory(rows); setHistMore(rows.length >= HIST_PAGE); })
      .catch(() => { setHistory([]); setHistMore(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Receipts trickle in for a few minutes after a send — keep the Sent list honest without a reload.
  // A message still going out refreshes faster so the progress count moves.
  useEffect(() => {
    const cutoff = Date.now() - 15 * 60000;
    const sending = (history || []).some((b) => b.status === "sending");
    const waiting = sending || (history || []).some((b) => b.status === "sent" && (b.stats?.sms_pending || 0) > 0 && new Date(b.sent_at || b.created_at).getTime() > cutoff);
    if (!waiting) return;
    const t = setInterval(loadHistory, sending ? 4000 : 15000);
    return () => clearInterval(t);
  }, [history, loadHistory]);

  const loadMoreHistory = async () => {
    setHistLoadingMore(true);
    try {
      const r = await api.get(`/tenants/${tenantId}/broadcasts?limit=${HIST_PAGE}&offset=${history.length}`);
      const rows = r.data || [];
      setHistory((h) => [...(h || []), ...rows]);
      setHistMore(rows.length >= HIST_PAGE);
    } catch { /* keep what we have */ }
    finally { setHistLoadingMore(false); }
  };

  // Reach, skip reasons, the exact SMS + its segment math and the quiet-hours snap — one debounced call.
  useEffect(() => {
    if (!tenantId || !audienceReady) { setPreview({}); return; }
    const t = setTimeout(() => {
      api.post(`/tenants/${tenantId}/broadcasts/preview`, payload()).then((r) => setPreview(r.data)).catch(() => setPreview({}));
    }, 300);
    return () => clearTimeout(t);
  }, [tenantId, audienceReady, payload]);

  const rephrase = async (style) => {
    if (!title.trim() && !message.trim()) { toast.error("Write a rough draft first — then AI can polish it"); return; }
    setRephrasing(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/rephrase`, { kind: urgent ? "safety" : "broadcast", title, description: message, style });
      setPendingRe({ style, title: (r.data.title || "").slice(0, 90), message: (r.data.description || "").slice(0, 1000) });
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't rephrase just now"); }
    finally { setRephrasing(false); }
  };
  const acceptRephrase = () => { setPreRephrase((p) => p || { title, message }); setTitle(pendingRe.title); setMessage(pendingRe.message); setPendingRe(null); toast.success("Rephrased — undo if you change your mind"); };
  const undoRephrase = () => { if (!preRephrase) return; setTitle(preRephrase.title); setMessage(preRephrase.message); setPreRephrase(null); };

  const normalize = async (which) => {
    setNormalizing(true);
    try {
      const src = which === "sms" ? smsBody : message;
      const r = await api.post(`/tenants/${tenantId}/sms-segments`, { text: src });
      (which === "sms" ? setSmsBody : setMessage)(r.data.normalized_text);
      toast.success("Swapped in plain quotes and dashes");
    } catch { toast.error("Couldn't normalize just now"); }
    setNormalizing(false);
  };

  const toolbar = (
    <div className="flex items-center gap-1.5">
      {preRephrase && !rephrasing && (
        <button type="button" data-testid="broadcast-rephrase-undo" onClick={undoRephrase} title="Restore your original wording"
          className="inline-flex items-center gap-1 rounded-full border border-sand-200 px-2.5 py-1 text-[11px] font-semibold text-ink/60 hover:border-ink hover:text-ink"><RotateCcw className="h-3 w-3" /> Undo</button>
      )}
      <RephraseStyleMenu busy={rephrasing} onPick={rephrase} testId="broadcast-rephrase" />
      <DictationMic testId="broadcast-mic" title="Dictate the message" onText={(t) => (split ? setSmsBody : setMessage)((m) => ((m.trim() ? m.trim() + " " : "") + t).slice(0, 1000))} />
    </div>
  );

  const reachSms = preview.sms || 0;
  const reachEmail = preview.email || 0;
  const totalReach = reachSms + reachEmail;
  const copyOk = smsText.trim() && message.trim();
  const canSend = copyOk && audienceReady && totalReach > 0 && (!schedule || !!scheduleAt);
  const cost = preview.cost || {};
  const snapped = schedule && preview.quiet_hours?.snapped;

  const reset = () => {
    setTitle(""); setMessage(""); setSmsBody(""); setConfirmOpen(false); setSchedule(false); setScheduleAt("");
    setPreRephrase(null); setPendingRe(null); setEditingId(null); setAudience(EMPTY_AUD); setKind("announcement"); setMode("email_first"); setStep(0);
  };

  const doSend = async () => {
    setSending(true);
    try {
      const r = editingId ? await api.put(`/tenants/${tenantId}/broadcasts/${editingId}`, payload()) : await api.post(`/tenants/${tenantId}/broadcasts`, payload());
      if (r.data.status === "scheduled") {
        toast.success(`${editingId ? "Updated — " : ""}Scheduled for ${fmtDate(r.data.scheduled_at, tz)}${tz ? ` ${tzLabel(tz)} time` : ""}${r.data.quiet_hours_snapped ? " (moved into texting hours)" : ""}`);
      } else if (r.data.status === "sending") {
        toast.success("Sending now — you can keep working. The Sent list fills in as each message goes out.");
      } else {
        const s = r.data.stats || {};
        toast.success(`Sent — ${s.sms_sent || 0} text${(s.sms_sent || 0) === 1 ? "" : "s"}, ${s.email_sent || 0} email${(s.email_sent || 0) === 1 ? "" : "s"}`);
      }
      reset(); loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the message"); }
    setSending(false);
  };

  const cancelScheduled = async (id) => {
    try { await api.delete(`/tenants/${tenantId}/broadcasts/${id}`); toast.success("Scheduled message cancelled"); if (editingId === id) reset(); loadHistory(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
  };

  const startEdit = async (b) => {
    setEditingId(b.id); setKind(b.kind || "announcement"); setSameCopy(b.same_copy !== false);
    setMode(b.delivery_mode || ((b.channels || []).length === 2 ? "both" : (b.channels || [])[0] === "email" ? "email_first" : "sms_first"));
    setTitle(b.title || ""); setMessage(b.email_body || b.body || ""); setSmsBody(b.sms_body || "");
    setSchedule(true); setScheduleAt(toLocalInput(b.scheduled_at, tz));
    const a = b.audience || { type: "all" };
    let people = [];
    if (a.type === "people" && a.person_ids?.length) {
      try { people = (await api.get(`/tenants/${tenantId}/messages/people?ids=${a.person_ids.join(",")}`)).data || []; } catch { people = []; }
    }
    setAudience({ type: a.type || "all", group_id: a.group_id || "", keys: a.keys || [], people });
    setStep(0);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const sendTest = async () => {
    if (!testPhone.trim()) return;
    try {
      const r = await api.post(`/tenants/${tenantId}/broadcasts`, { ...payload(), channels: ["sms"], delivery_mode: "both", test_phone: testPhone, scheduled_at: undefined });
      toast[r.data.delivered ? "success" : "error"](r.data.delivered ? "Test text sent" : "Test failed — check the number / Twilio setup");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };

  const verb = editingId ? "Save changes" : schedule ? "Schedule" : urgent ? "Send safety alert" : "Send now";
  const stepOk = [audienceReady, true, !!copyOk, !!canSend][step];
  const blocker = [audience.type === "group" ? "Pick a group" : audience.type === "people" ? `Pick at least one ${V.lc("person")}` : audience.type === "audiences" && !(audience.keys || []).length ? "Pick at least one audience" : "",
    "", !smsText.trim() ? "Write the text" : !message.trim() ? "Write the email" : "",
    schedule && !scheduleAt ? "Pick a date and time" : totalReach === 0 && audienceReady ? "Nobody can be reached — check who's opted in" : ""][step];
  const goNext = () => { if (stepOk) { setStep((i) => Math.min(i + 1, 3)); topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } };
  const goBack = () => setStep((i) => Math.max(i - 1, 0));
  const submit = () => { if (urgent) setConfirmOpen(true); else doSend(); };

  return (
    <div className="max-w-[960px] mx-auto" ref={topRef}>
      <div className="mb-6">
        <PageTitle title={<><MessageSquareText className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Messages</>} about={<>Text or email all your {V.lcs("people")}, a group, or one person — now or scheduled. Four quick steps.</>} />
      </div>

      <MessagesIntro />

      <div data-testid="message-composer" className={`bg-white rounded-2xl border p-5 mb-8 ${editingId ? "border-blue-300 ring-2 ring-blue-100" : "border-sand-200"}`}>
        {editingId && (
          <div data-testid="editing-banner" className="flex items-center justify-between gap-2 rounded-xl bg-blue-50 border border-blue-200 px-3.5 py-2 mb-4 text-sm text-blue-800">
            <span className="flex items-center gap-2"><Pencil className="h-4 w-4" /> Editing a scheduled message</span>
            <button type="button" data-testid="editing-discard" onClick={reset} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline"><X className="h-3.5 w-3.5" /> Discard</button>
          </div>
        )}
        <StepHeader idx={step} onJump={setStep} />

        <div className="mt-5 min-h-[220px]">
          <StepBody idx={step}>
            {step === 0 && (
              <div data-testid="step-who">
                <h2 className="font-heading text-lg font-bold text-ink mb-3">Who is this for?</h2>
                <AudiencePicker tenantId={tenantId} audience={audience} onChange={setAudience} groups={groups} onManageGroups={() => setGroupsOpen(true)} />
                {audience.type === "all" && (
                  <div className="mt-5">
                    <label className="block text-xs font-bold text-ink/50 mb-2">What kind of message</label>
                    <div className="flex flex-wrap gap-2">
                      <Pill testId="broadcast-kind-announcement" on={kind === "announcement"} onClick={() => setKind("announcement")} icon={Megaphone}>Announcement</Pill>
                      <Pill testId="broadcast-kind-safety" on={kind === "safety"} onClick={() => setKind("safety")} icon={ShieldAlert}>Urgent safety alert</Pill>
                    </div>
                  </div>
                )}
                {urgent && (
                  <div data-testid="broadcast-safety-note" className="flex items-start gap-2 rounded-xl bg-redline-50 border border-redline-200 px-4 py-3 mt-3 text-sm text-redline-800">
                    <AlertTriangle className="h-4.5 w-4.5 mt-0.5 shrink-0" />
                    <span>Safety alerts go to <strong>every {V.lc("person")} phone on file</strong>, bypassing marketing opt-in. Only {V.lcs("person")} who texted STOP are excluded. Use this only for genuine safety situations.</span>
                  </div>
                )}
                {audienceReady && preview.audience_label && (
                  <p data-testid="step-who-summary" className="mt-4 text-sm text-ink/60"><Users className="inline h-4 w-4 mr-1 -mt-0.5 text-ink/40" /><span className="font-semibold text-ink">{preview.audience_label}</span>{preview.audience_size ? ` · ${preview.audience_size} ${preview.audience_size === 1 ? "person" : "people"}` : ""}</p>
                )}
              </div>
            )}

            {step === 1 && (
              <div data-testid="step-how">
                <h2 className="font-heading text-lg font-bold text-ink mb-3">How should it go out?</h2>
                <DeliveryModePicker mode={effMode} onChange={setMode} locked={urgent} />
                {audienceReady && (
                  <div data-testid="step-how-reach" className="mt-4 rounded-xl bg-sand-50 border border-sand-200 px-4 py-3 text-sm text-ink/70">
                    <span className="font-semibold text-ink">{preview.audience_label || "Everyone"}</span> — <span className="font-semibold text-ink">{reachEmail}</span> by email · <span className="font-semibold text-ink">{reachSms}</span> by text{effMode === "email_first" && reachSms > 0 ? " (no email on file)" : ""}
                    {reachSms > 0 && cost.segments > 0 && message.trim() && <span className="text-ink/50"> · texts about ${cost.usd.toFixed(2)}</span>}
                    {audienceReady && preview.skipped && skipSummary(preview.skipped) && (
                      <div data-testid="broadcast-skipped" className="text-[11px] text-ink/45 mt-1">
                        Skipped: {preview.skipped_people?.length > 0 && preview.skipped_people.length <= 6
                          ? preview.skipped_people.map((p) => `${p.name} (${p.label.toLowerCase()})`).join(" · ")
                          : skipSummary(preview.skipped)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-write">
                <h2 className="font-heading text-lg font-bold text-ink mb-3">What do you want to say?</h2>
                <CopyEditor channels={channels} sameCopy={sameCopy} setSameCopy={setSameCopy} title={title} setTitle={setTitle} message={message} setMessage={setMessage}
                  smsBody={smsBody} setSmsBody={setSmsBody} meta={preview} onNormalize={normalize} normalizing={normalizing}
                  personal={personal} urgent={urgent} toolbar={toolbar} smsPreview={smsText.trim() ? preview.sms_preview : ""} />
                {pendingRe && (
                  <div data-testid="broadcast-rephrase-preview" className="mt-2 rounded-xl border border-sand-200 bg-sand-50 p-3">
                    <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-ink/40">Suggested rewrite — nothing changes until you accept</div>
                    {pendingRe.title && <div className="text-sm font-bold text-ink">{pendingRe.title}</div>}
                    <div className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-ink/75">{pendingRe.message}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <button type="button" data-testid="broadcast-rephrase-accept" onClick={acceptRephrase} className="rounded-full bg-redline-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-redline-600">Use this</button>
                      <button type="button" data-testid="broadcast-rephrase-reject" onClick={() => setPendingRe(null)} className="rounded-full border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/55 hover:border-ink/30">Keep mine</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-review">
                <h2 className="font-heading text-lg font-bold text-ink mb-3">When should it go — and does it look right?</h2>
                <div className="flex flex-wrap items-center gap-2">
                  {!editingId && <Pill testId="broadcast-mode-now" on={!schedule} onClick={() => setSchedule(false)} icon={Send}>Send now</Pill>}
                  <Pill testId="broadcast-mode-schedule" on={schedule} onClick={() => setSchedule(true)} icon={CalendarClock}>Schedule for later</Pill>
                  {schedule && (
                    <div className="flex flex-wrap items-center gap-2">
                      <input data-testid="broadcast-schedule-at" type="datetime-local" value={scheduleAt} min={minLocalInput()} onChange={(e) => setScheduleAt(e.target.value)}
                        className="rounded-lg border border-sand-200 px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                      {tz && <span data-testid="broadcast-schedule-tz" className="text-xs font-semibold text-ink/50">{tzLabel(tz)} time (the {V.lc("org")}'s)</span>}
                    </div>
                  )}
                </div>
                {snapped && (
                  <div data-testid="quiet-hours-note" className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-2.5 text-xs text-amber-800">
                    <MoonStar className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>Texts don't go out between 9 PM and 8 AM {tz ? tzLabel(tz) : `${V.lc("org")}`} time — this one will send at <strong>{fmtDate(preview.quiet_hours.scheduled_at, tz)}</strong> instead.</span>
                  </div>
                )}

                <div data-testid="review-summary" className="mt-5 grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl border border-sand-200 p-3.5 text-sm space-y-1.5">
                    <div className="text-[11px] font-black uppercase tracking-wider text-ink/40">Summary</div>
                    <div><span className="text-ink/50">To</span> <span className="font-semibold text-ink" data-testid="review-audience">{preview.audience_label || "Everyone"}</span>{urgent && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-redline-50 text-redline-700 px-2 py-0.5 text-[11px] font-bold"><ShieldAlert className="h-3 w-3" /> Safety alert</span>}</div>
                    <div><span className="text-ink/50">How</span> <span className="font-semibold text-ink">{modeLabel(effMode)}</span></div>
                    <div data-testid="broadcast-reach"><span className="text-ink/50">Reach</span> <span className="font-semibold text-ink">{reachEmail} by email · {reachSms} by text</span>{effMode === "email_first" && reachSms > 0 && <span className="text-ink/45"> (no email on file)</span>}</div>
                    {reachSms > 0 && cost.segments > 0 && (
                      <div data-testid="broadcast-cost" className={cost.usd >= 1 ? "text-amber-700 font-semibold" : "text-ink/60"}>
                        <span className="text-ink/50 font-normal">Cost</span> texts ≈ {cost.segments} segment{cost.segments === 1 ? "" : "s"} · about ${cost.usd.toFixed(2)}{effMode !== "email_first" ? " — Email first would cost nothing for anyone with an email" : ""}
                      </div>
                    )}
                    {audienceReady && preview.skipped && skipSummary(preview.skipped) && (
                      <div data-testid="broadcast-skipped" className="text-[11px] text-ink/45">
                        Skipped: {preview.skipped_people?.length > 0 && preview.skipped_people.length <= 6
                          ? preview.skipped_people.map((p) => `${p.name} (${p.label.toLowerCase()})`).join(" · ")
                          : skipSummary(preview.skipped)}
                      </div>
                    )}
                    <div><span className="text-ink/50">When</span> <span className="font-semibold text-ink">{schedule ? (scheduleAt ? fmtDate(snapped ? preview.quiet_hours.scheduled_at : scheduleAt, snapped ? tz : undefined) + (tz ? ` ${tzLabel(tz)}` : "") : "pick a time") : "right away"}</span></div>
                  </div>
                  <div className="space-y-2">
                    {reachSms > 0 && preview.sms_preview && (
                      <div data-testid="review-sms" className="rounded-xl bg-ink text-white p-3 text-[13px] whitespace-pre-wrap"><div className="text-[10px] uppercase tracking-wider text-white/50 mb-1 flex items-center gap-1"><MessageSquareText className="h-3 w-3" /> Text{preview.segments ? ` · ${preview.segments} segment${preview.segments === 1 ? "" : "s"}` : ""}</div>{preview.sms_preview}</div>
                    )}
                    {reachEmail > 0 && (
                      <div data-testid="review-email" className="rounded-xl bg-sand-50 border border-sand-200 p-3 text-ink/80 whitespace-pre-wrap text-sm"><div className="text-[10px] uppercase tracking-wider text-ink/40 mb-1 flex items-center gap-1"><Mail className="h-3 w-3" /> Email</div>{title && <div className="font-semibold">{title}</div>}{message}</div>
                    )}
                  </div>
                </div>

                {!editingId && reachSms > 0 && (
                  <div className="mt-4 pt-4 border-t border-sand-100 flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[180px]">
                      <label className="block text-[11px] font-bold text-ink/40 mb-1">Send a test text to yourself first</label>
                      <input data-testid="broadcast-test-phone" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+15125550123"
                        className="w-full rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                    </div>
                    <button data-testid="broadcast-test-send" onClick={sendTest} disabled={!smsText.trim() || !testPhone.trim()}
                      className="rounded-full bg-white border border-sand-200 text-ink font-semibold px-4 py-2 text-sm hover:bg-sand-50 disabled:opacity-50">Send test</button>
                  </div>
                )}
              </div>
            )}
          </StepBody>
        </div>

        <WizardFooter idx={step} canNext={!!stepOk} blocker={blocker} onBack={goBack} onNext={goNext} onSubmit={submit} submitting={sending} danger={urgent}
          submitLabel={<>{schedule ? <CalendarClock className="h-4 w-4" /> : <Send className="h-4 w-4" />} {verb}</>} />
      </div>

      <MessageHistory history={history} tz={tz} tenantId={tenantId} onCancel={cancelScheduled} onEdit={startEdit} hasMore={histMore} onLoadMore={loadMoreHistory} loadingMore={histLoadingMore} />

      <GroupsDialog open={groupsOpen} onOpenChange={setGroupsOpen} tenantId={tenantId} groups={groups} reload={loadGroups} />

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="broadcast-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-danger-600" /> Send urgent safety alert?</DialogTitle>
          </DialogHeader>
          <DialogDescription className="sr-only">Confirm before it goes to every {V.lc("person")} phone.</DialogDescription>
          <div className="text-sm text-ink/70 space-y-2">
            <p><strong>{preview.audience_label || "Everyone"}</strong> — <strong>{reachSms}</strong> by text and <strong>{reachEmail}</strong> by email{schedule && scheduleAt ? <>, <strong>{fmtDate(scheduleAt)}</strong></> : null}.</p>
            {preview.sms_preview && <div className="rounded-xl bg-ink text-white p-3 text-[13px] whitespace-pre-wrap">{preview.sms_preview}</div>}
            <p className="text-danger-700 text-xs">Safety alerts bypass marketing opt-in. Send only for genuine safety situations.</p>
          </div>
          <DialogFooter>
            <button onClick={() => setConfirmOpen(false)} className="rounded-full bg-white border border-sand-200 text-ink font-semibold px-5 py-2 text-sm hover:bg-sand-50">Back</button>
            <button data-testid="broadcast-confirm-send" onClick={doSend} disabled={sending}
              className="rounded-full font-semibold px-6 py-2 text-sm text-white flex items-center gap-2 disabled:opacity-50 bg-danger-600 hover:bg-danger-700">
              <Send className="h-4 w-4" /> {sending ? "Working…" : "Send now"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
